import { PartnerNotification } from '../types';
import { audioService } from './audio';

export interface PartnerWsCallbacks {
  onNotification?: (notification: PartnerNotification) => void;
  onConnected?: (user: any) => void;
  onError?: (err: string) => void;
  onClose?: () => void;
}

export class PartnerWsClient {
  private ws: WebSocket | null = null;
  private isExplicitlyClosed = false;
  private callbacks: PartnerWsCallbacks = {};
  private activeVenueId?: string;
  private activeToken = '';

  public connect(token: string, venueId?: string, callbacks: PartnerWsCallbacks = {}): void {
    this.activeToken = token;
    this.activeVenueId = venueId;
    this.callbacks = callbacks;
    this.isExplicitlyClosed = false;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/partner/ws?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        if (this.activeVenueId && this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'subscribe_venue',
            venueId: this.activeVenueId
          }));
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'connected') {
            if (this.callbacks.onConnected) {
              this.callbacks.onConnected(msg.user);
            }
          } else if (msg.type === 'notification' && msg.notification) {
            // Play notification chime
            audioService.playNotificationChime();
            if (this.callbacks.onNotification) {
              this.callbacks.onNotification(msg.notification);
            }
          }
        } catch (e) {
          console.error('[PartnerWS] Failed to parse message:', e);
        }
      };

      this.ws.onerror = () => {
        if (this.callbacks.onError) {
          this.callbacks.onError('Partner WebSocket error');
        }
      };

      this.ws.onclose = () => {
        if (this.callbacks.onClose) {
          this.callbacks.onClose();
        }

        if (!this.isExplicitlyClosed) {
          setTimeout(() => {
            if (!this.isExplicitlyClosed && this.activeToken) {
              this.connect(this.activeToken, this.activeVenueId, this.callbacks);
            }
          }, 3000);
        }
      };
    } catch (e: any) {
      if (this.callbacks.onError) {
        this.callbacks.onError(e?.message || 'Failed to initialize partner WebSocket');
      }
    }
  }

  public subscribeVenue(venueId: string): void {
    this.activeVenueId = venueId;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe_venue',
        venueId
      }));
    }
  }

  public close(): void {
    this.isExplicitlyClosed = true;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }
  }
}

export const partnerWsClient = new PartnerWsClient();
